import { observer } from 'mobx-react-lite';
/* tslint:disable:jsx-no-lambda WI:47690 */
import { navigationPaneLabel } from './MailModule.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';
import folderStore from 'owa-folders';
import { globalStyles } from 'owa-global-styles';
import groupHeaderCommandBarStore from 'owa-group-header-store/lib/store/CommandBarStore';
import GroupHeaderNavigationButton from 'owa-group-header-store/lib/store/schema/NavigationButton';
import { getSelectedGroupId } from 'owa-group-utils';
import { LeftRail } from 'owa-left-rail';
import { isLeftRailVisible } from 'owa-left-rail-utils/lib/isLeftRailVisible';
import loc from 'owa-localize';
import NewMessageButton from 'owa-mail-commandbar/lib/components/NewMessageButton';
import { lazyOpenCompose } from 'owa-mail-compose-actions';
import selectDefaultFolder from 'owa-mail-folder-forest-actions/lib/actions/selectDefaultFolder';
import { getSelectedTableView } from 'owa-mail-list-store';
import checkAndCreateNewMessage from 'owa-mail-message-actions/lib/actions/checkAndCreateNewMessage';
import { Module } from 'owa-bootstrap';
import { lazyCreateNewNote } from 'owa-notes-store';
import { getSelectedPublicFolderId } from 'owa-publicfolder-utils';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import * as React from 'react';
import {
    getLeftNavWidth,
    getDataMinMaxWidthsForLeftNav,
    shouldShowFolderPaneAsOverlay,
    shouldShowFolderPane,
    getLeftPaneStyles,
} from 'owa-mail-layout';
import { MailLeftPaneAsOverlay } from 'owa-mail-module-view'; // lazy import overlay left pane component
import FolderPaneContainer from './FolderPaneContainer';
import { NEWS_ID } from 'owa-mail-news';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import { logUsage } from 'owa-analytics';
import LeftPaneHamburgerButton from 'owa-mail-commandbar/lib/components/LeftPaneHamburgerButton';

interface MailLeftPaneProps {
    isInGroupsView: boolean;
    shouldShowPublicFolderView: boolean;
    hideLeftRail?: boolean;
}

const styles = require('./MailModule.scss');
const classNames = require('classnames/bind').bind(styles);

export default observer(function MailLeftPane(props: MailLeftPaneProps) {
    /**
     * Renders the hamburger button and the new message button
     */
    const renderToggleAndNewMessageButtons = (
        showFolderPane: boolean,
        showFolderPaneAsOverlay: boolean
    ): JSX.Element => {
        const currentView = groupHeaderCommandBarStore.navigationButtonSelected;
        const inGroupFiles =
            props.isInGroupsView && currentView == GroupHeaderNavigationButton.Files;
        const tableView = getSelectedTableView();
        const sourceFolder = folderStore.folderTable.get(tableView.tableQuery.folderId);

        // Show 'New Note' instead of 'New Message' when notes is selected and when News is not selected.
        // We need to check if News is selected here since it's currently possible for the selected folder
        // to be Notes if the user was in Notes before navigating to News. We don't change the selected folder
        // when switching to News
        const shouldShowNewNotesButton =
            !!sourceFolder &&
            folderIdToName(sourceFolder.FolderId.Id) == 'notes' &&
            isFeatureEnabled('notes-folder-view') &&
            getSelectedNode().id !== NEWS_ID;
        const shouldShowNewMessageButton = showFolderPane && !showFolderPaneAsOverlay;
        return (
            <div className={styles.leftPanelFirstRow}>
                <LeftPaneHamburgerButton isPaneExpanded={showFolderPane} />
                {shouldShowNewMessageButton && (
                    <div className={styles.newMessageButtonWrapper}>
                        {shouldShowNewNotesButton ? (
                            <NewMessageButton scenario={'notes'} onClick={onNewNoteButtonClicked} />
                        ) : inGroupFiles ? null : (
                            <NewMessageButton
                                scenario={
                                    props.shouldShowPublicFolderView ? 'publicFolder' : 'default'
                                }
                                onClick={onNewMessageButtonClicked}
                                onMouseOver={lazyOpenCompose.import}
                            />
                        )}
                    </div>
                )}
            </div>
        );
    };

    const onNewMessageButtonClicked = () => {
        const targetId: string = props.shouldShowPublicFolderView
            ? getSelectedPublicFolderId()
            : !isFeatureEnabled('grp-groupHeaderV2')
            ? getSelectedGroupId()
            : null;
        if (getSelectedNode().id === NEWS_ID) {
            logUsage('NewsNewMessageClick');
            selectDefaultFolder('CommandBar');
        }
        checkAndCreateNewMessage('CommandBar', targetId);
    };
    const showFolderPane = shouldShowFolderPane();
    const showFolderPaneAsOverlay = shouldShowFolderPaneAsOverlay();
    const leftPaneClasses = classNames(
        styles.leftPaneContainer,
        globalStyles.addFocusOutline,
        getLeftPaneStyles()
    );
    const { dataMin, dataMax } = getDataMinMaxWidthsForLeftNav();
    const showLeftRail =
        !props.hideLeftRail && (!showFolderPane || isLeftRailVisible() || showFolderPaneAsOverlay);
    return (
        <div
            tabIndex={-1}
            role={'region'}
            aria-label={loc(navigationPaneLabel)}
            className={leftPaneClasses}
            data-min-width={dataMin}
            data-max-width={dataMax}>
            {renderToggleAndNewMessageButtons(showFolderPane, showFolderPaneAsOverlay)}
            <div className={styles.leftPanelSecondRow}>
                {showLeftRail && (
                    <LeftRail selectedModule={Module.Mail} activeModuleAction={onModuleSelected} />
                )}
                {showFolderPane && renderFolderPane(showLeftRail, showFolderPaneAsOverlay)}
            </div>
        </div>
    );
});

function onModuleSelected() {
    selectDefaultFolder('ModuleSwitcher');
}

/**
 * Renders the folder pane as an overlay or non-overlay
 */
function renderFolderPane(isLeftRailVisible: boolean, showFolderPaneAsOverlay: boolean) {
    if (!showFolderPaneAsOverlay) {
        return (
            <FolderPaneContainer
                leftNavWidth={getLeftNavWidth()}
                isLeftRailVisible={isLeftRailVisible}
            />
        );
    }
    return <MailLeftPaneAsOverlay isLeftRailVisible={isLeftRailVisible} />;
}

function onNewNoteButtonClicked() {
    lazyCreateNewNote.importAndExecute();
}
